import { css } from '@emotion/react';
import { FMT } from '../../../util/formatter-service';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AgTable } from '../../../components/AgTable/AgTable';
import { FadeInGridWrapper } from '../../../components/grid-renderers/FadeInGridWrapper';
import { Loader } from '../../../components/Loader/Loader';
import {
  BulkUploadDetail,
  BulkUploadItem,
  getBulkUploadDetail,
  getBulkUploadItems,
} from '../../../services/queries/BulkDealDocsQueries';
import { selectedBulkDealDocsActionState, selectedBulkUploadIdState } from './bulkDealDocsState';
import { BULK_UPLOAD_CONFIG } from './constants';
import { BulkUploadLogger } from './logging';

export function BulkUploadDetailsModal() {
  const theme = useTheme();
  const [selectedAction, setSelectedAction] = useAtom(selectedBulkDealDocsActionState);
  const uploadId = useAtomValue(selectedBulkUploadIdState);
  const [detail, setDetail] = useState<BulkUploadDetail | null>(null);
  const [items, setItems] = useState<BulkUploadItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(BULK_UPLOAD_CONFIG.DETAILS_PAGE_SIZE);
  const [loading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);

  const open = selectedAction === 'view' && uploadId !== null;

  // Map tab index to status filter
  const getStatusFilter = (tabIndex: number): string | undefined => {
    if (tabIndex === 0) {
      return undefined;
    } // All
    if (tabIndex === 1) {
      return 'success';
    }
    if (tabIndex === 2) {
      return 'failed';
    }
    if (tabIndex === 3) {
      return 'skipped';
    }
    return 'pending'; // Pending/Processing
  };

  const loadDetail = useCallback(async () => {
    if (!uploadId) {
      return;
    }

    try {
      const detailData = await getBulkUploadDetail(uploadId);
      setDetail(detailData);
      setError(null);
    } catch (err) {
      BulkUploadLogger.error('Failed to load bulk upload details', err, { uploadId });
      setError(err instanceof Error ? err.message : 'Failed to load details');
    }
  }, [uploadId]);

  const loadItems = useCallback(async () => {
    if (!uploadId) {
      return;
    }

    setItemsLoading(true);
    setError(null);

    try {
      const statusFilter = getStatusFilter(selectedTab);
      const itemsData = await getBulkUploadItems(uploadId, page, pageSize, statusFilter);

      setItems(itemsData.items);
      setTotalItems(itemsData.pagination.total);
    } catch (err) {
      BulkUploadLogger.error('Failed to load bulk upload items', err, { uploadId, page });
      setError(err instanceof Error ? err.message : 'Failed to load items');
    } finally {
      setItemsLoading(false);
    }
  }, [uploadId, selectedTab, page, pageSize]);

  // Load detail summary when uploadId changes
  useEffect(() => {
    if (open && uploadId) {
      setDetail(null); // Reset detail when uploadId changes
      loadDetail();
    }
  }, [open, uploadId, loadDetail]);

  // Load items when page, tab, or uploadId changes
  useEffect(() => {
    if (open && uploadId) {
      loadItems();
    }
  }, [open, uploadId, loadItems]);

  const handleClose = () => {
    setSelectedAction(null);
    setDetail(null);
    setItems([]);
    setTotalItems(0);
    setPage(1);
    setSelectedTab(0);
    setError(null);
  };

  const handleTabChange = (_: unknown, newValue: number) => {
    setSelectedTab(newValue);
    setPage(1); // Reset to first page when changing tabs
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRefresh = async () => {
    await Promise.all([loadDetail(), loadItems()]);
  };

  const statusStyles = useMemo(
    () => ({
      pending: css`
        color: ${theme.palette.text.secondary};
        font-weight: bold;
      `,
      processing: css`
        color: ${theme.palette.info.main};
        font-weight: bold;
      `,
      success: css`
        color: ${theme.palette.success.main};
        font-weight: bold;
      `,
      failed: css`
        color: ${theme.palette.error.main};
        font-weight: bold;
      `,
      skipped: css`
        color: ${theme.palette.warning.main};
        font-weight: bold;
      `,
    }),
    [theme]
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: 'fileName',
        headerName: 'File Name',
        minWidth: 200,
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        cellRenderer: (params: ICellRendererParams<BulkUploadItem>) => {
          if (!params.value) {
            return null;
          }
          const status = params.value as keyof typeof statusStyles;
          const style = statusStyles[status];
          return <span css={style}>{params.value.toUpperCase()}</span>;
        },
      },
      {
        field: 'companyNameParsed',
        headerName: 'Company',
        width: 150,
        valueFormatter: (params: ValueFormatterParams<BulkUploadItem>) => params.value || '-',
      },
      {
        field: 'roundNameParsed',
        headerName: 'Round',
        width: 120,
        valueFormatter: (params: ValueFormatterParams<BulkUploadItem>) => params.value || '-',
      },
      {
        field: 'documentType',
        headerName: 'Type',
        width: 110,
        valueFormatter: (params: ValueFormatterParams<BulkUploadItem>) => params.value || '-',
      },
      {
        field: 'errorMessage',
        headerName: 'Error/Notes',
        minWidth: 300,
        flex: 2,
        valueFormatter: (params: ValueFormatterParams<BulkUploadItem>) => params.value || '-',
      },
      {
        field: 'filePath',
        headerName: 'Path in ZIP',
        minWidth: 300,
        flex: 2,
      },
    ],
    [statusStyles]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    []
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xl' fullWidth>
      <DialogTitle>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography variant='h5'>Bulk Upload Details</Typography>
          <Stack direction='row' spacing={1}>
            <IconButton onClick={handleRefresh} disabled={loading || itemsLoading} title='Refresh'>
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {loading && <Loader />}
        {error && <Alert severity='error'>{error}</Alert>}

        {detail && !loading && (
          <Stack spacing={2}>
            <Box>
              <Typography variant='h6' gutterBottom>
                {detail.fileName}
              </Typography>
              <Stack direction='row' spacing={2} alignItems='center'>
                <Chip
                  label={detail.status.toUpperCase()}
                  color={
                    detail.status === 'completed'
                      ? 'success'
                      : detail.status === 'error'
                        ? 'error'
                        : detail.status === 'processing'
                          ? 'info'
                          : 'default'
                  }
                />
                <Typography variant='body2' color='text.secondary'>
                  {detail.processedFiles} / {detail.totalFiles} files processed
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Uploaded by {detail.uploadedBy}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {FMT.format('dateTime', detail.createdAt)}
                </Typography>
              </Stack>
            </Box>

            <Stack direction='row' spacing={2}>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Success
                </Typography>
                <Typography variant='h6' color='success.main'>
                  {detail.successCount}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Failed
                </Typography>
                <Typography variant='h6' color='error.main'>
                  {detail.failedCount}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Skipped
                </Typography>
                <Typography variant='h6' color='text.secondary'>
                  {detail.skippedCount}
                </Typography>
              </Box>
            </Stack>

            {detail.errorMessage && (
              <Alert severity='error'>
                <Typography variant='body2'>{detail.errorMessage}</Typography>
              </Alert>
            )}

            <Box>
              <Tabs value={selectedTab} onChange={handleTabChange}>
                <Tab label={`All (${detail.totalFiles})`} />
                <Tab label={`Success (${detail.successCount})`} />
                <Tab label={`Failed (${detail.failedCount})`} />
                <Tab label={`Skipped (${detail.skippedCount})`} />
                <Tab label={`Pending (${detail.totalFiles - detail.processedFiles})`} />
              </Tabs>
            </Box>

            <FadeInGridWrapper>
              <Stack height='650px' spacing={2}>
                {itemsLoading && <Loader />}
                {!itemsLoading && (
                  <AgTable
                    columnDefs={columnDefs}
                    rowData={items}
                    defaultColDef={defaultColDef}
                    rowGroupPanelShow='never'
                    suppressPaginationPanel
                  />
                )}
                {!itemsLoading && totalItems > pageSize && (
                  <Stack direction='row' justifyContent='space-between' alignItems='center' px={2}>
                    <Typography variant='body2' color='text.secondary'>
                      Showing {items.length === 0 ? 0 : (page - 1) * pageSize + 1} to{' '}
                      {Math.min(page * pageSize, totalItems)} of {totalItems} items
                    </Typography>
                    <Stack direction='row' spacing={1}>
                      <Button
                        size='small'
                        variant='outlined'
                        disabled={page === 1 || itemsLoading}
                        onClick={() => handlePageChange(page - 1)}
                      >
                        Previous
                      </Button>
                      <Typography variant='body2' sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                        Page {page} of {Math.ceil(totalItems / pageSize)}
                      </Typography>
                      <Button
                        size='small'
                        variant='outlined'
                        disabled={page >= Math.ceil(totalItems / pageSize) || itemsLoading}
                        onClick={() => handlePageChange(page + 1)}
                      >
                        Next
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </FadeInGridWrapper>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
